import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { MapPin, Radio } from "lucide-react";
import { api } from "@/lib/api";
import TeamCrest from "@/components/TeamCrest";
import PotgCard from "@/components/PotgCard";
import GameStats from "@/components/GameStats";
import EpicMatchup from "@/components/EpicMatchup";
import PlayoffBracket from "@/components/PlayoffBracket";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

function GameCard({ game }) {
    const isFinal = game.status === "final";
    const homeWon = isFinal && game.home_score > game.away_score;
    const awayWon = isFinal && game.away_score > game.home_score;
    return (
        <div data-testid={`game-card-${game.id}`} className="card-arena rounded-2xl p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
                <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                    {format(new Date(game.date), "EEE, MMM d • h:mm a")}
                    {game.week ? ` • Week ${game.week}` : ""}
                </span>
                {game.status === "live" ? (
                    <span className="flex items-center gap-1.5 text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#EF4444]/15 text-[#EF4444] border border-[#EF4444]/50">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#EF4444] animate-pulse" />
                        Live{game.quarter ? ` • ${game.quarter}` : ""}
                        {game.clock ? ` ${game.clock}` : ""}
                    </span>
                ) : isFinal ? (
                    <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#22C55E]/15 text-[#22C55E] border border-[#22C55E]/40">
                        Final
                    </span>
                ) : (
                    <span className="text-[10px] font-display font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FFB800]/10 text-[#FFB800] border border-[#FFB800]/40">
                        Upcoming
                    </span>
                )}
            </div>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                <div className="flex flex-col items-center gap-2 text-center">
                    <TeamCrest team={game.home_team} size="lg" />
                    <span
                        className={`font-display font-bold uppercase text-sm leading-tight ${
                            homeWon ? "text-white" : "text-slate-400"
                        }`}
                    >
                        {game.home_team?.name}
                    </span>
                </div>
                <div className="text-center px-2">
                    {game.status === "live" ? (
                        <div className="font-stat font-bold text-3xl sm:text-4xl">
                            <span className="text-[#EF4444]">{game.home_score ?? 0}</span>
                            <span className="text-slate-600 mx-2">:</span>
                            <span className="text-[#EF4444]">{game.away_score ?? 0}</span>
                        </div>
                    ) : isFinal ? (
                        <div className="font-stat font-bold text-3xl sm:text-4xl animate-score-flicker">
                            <span className={homeWon ? "text-[#FF5500]" : "text-slate-400"}>{game.home_score}</span>
                            <span className="text-slate-600 mx-2">:</span>
                            <span className={awayWon ? "text-[#FF5500]" : "text-slate-400"}>{game.away_score}</span>
                        </div>
                    ) : (
                        <span className="font-display font-black text-3xl text-stroke-faint">VS</span>
                    )}
                </div>
                <div className="flex flex-col items-center gap-2 text-center">
                    <TeamCrest team={game.away_team} size="lg" />
                    <span
                        className={`font-display font-bold uppercase text-sm leading-tight ${
                            awayWon ? "text-white" : "text-slate-400"
                        }`}
                    >
                        {game.away_team?.name}
                    </span>
                </div>
            </div>
            {(game.venue || game.status === "live") && (
                <div className="flex items-center justify-center gap-4 mt-5 pt-4 border-t border-[#262638]">
                    {game.venue && (
                        <p className="flex items-center gap-1.5 text-xs text-slate-500">
                            <MapPin className="w-3.5 h-3.5 text-[#FF5500]" /> {game.venue}
                        </p>
                    )}
                    {game.status === "live" && (
                        <Link
                            to="/live"
                            data-testid={`watch-live-${game.id}`}
                            className="flex items-center gap-1.5 text-xs font-display font-bold uppercase tracking-widest text-[#EF4444] hover:text-white transition-colors duration-200"
                        >
                            <Radio className="w-3.5 h-3.5" /> Watch Live
                        </Link>
                    )}
                </div>
            )}
            <PotgCard game={game} />
            <GameStats game={game} />
        </div>
    );
}

export default function Schedule() {
    const [games, setGames] = useState(null);
    const [tab, setTab] = useState("scheduled");

    useEffect(() => {
        const load = () => api.get("/games").then((r) => setGames(r.data)).catch(() => setGames([]));
        load();
        const t = setInterval(load, 10000);
        return () => clearInterval(t);
    }, []);

    const filtered = (games || [])
        .filter((g) => (tab === "all" ? true : g.status === tab))
        .sort((a, b) => (a.status === "live" ? -1 : b.status === "live" ? 1 : 0));

    return (
        <div data-testid="schedule-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#FF5500] mb-3">Fixtures & Results</p>
            <h1 className="font-display font-black uppercase text-4xl sm:text-5xl text-white">Schedule</h1>

            <Tabs value={tab} onValueChange={setTab} className="mt-10">
                <TabsList className="bg-[#12121A] border border-[#262638] flex-wrap h-auto">
                    <TabsTrigger data-testid="schedule-tab-all" value="all" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                        All Games
                    </TabsTrigger>
                    <TabsTrigger data-testid="schedule-tab-live" value="live" className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white">
                        Live
                    </TabsTrigger>
                    <TabsTrigger data-testid="schedule-tab-upcoming" value="scheduled" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                        Upcoming
                    </TabsTrigger>
                    <TabsTrigger data-testid="schedule-tab-final" value="final" className="data-[state=active]:bg-[#FF5500] data-[state=active]:text-white">
                        Results
                    </TabsTrigger>
                    <TabsTrigger data-testid="schedule-tab-playoffs" value="playoffs" className="data-[state=active]:bg-[#FFB800] data-[state=active]:text-black">
                        Playoffs
                    </TabsTrigger>
                </TabsList>
            </Tabs>

            {tab === "playoffs" ? (
                <PlayoffBracket />
            ) : games === null ? (
                <p className="mt-16 text-slate-500 font-stat text-sm">Loading schedule…</p>
            ) : filtered.length === 0 ? (
                <div data-testid="schedule-empty" className="mt-10 card-arena rounded-2xl p-12 text-center">
                    <p className="font-display font-bold uppercase text-xl text-slate-300">No games here yet</p>
                </div>
            ) : (
                <>
                    {tab === "scheduled" && (
                        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {filtered.slice(0, 3).map((g, i) => (
                                <div key={g.id}>
                                    <p
                                        data-testid={`upcoming-header-${i + 1}`}
                                        className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-[#FF5500] flex items-center gap-2"
                                    >
                                        <span className="w-5 h-px bg-[#FF5500]/60" />
                                        {["1st Game", "2nd Game", "3rd Game"][i]}
                                    </p>
                                    <EpicMatchup game={g} />
                                </div>
                            ))}
                        </div>
                    )}
                    {tab !== "scheduled" && (
                        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {filtered.map((g) => (
                                <GameCard key={g.id} game={g} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
